/**
 * First-login password change wall.
 *
 * Shown when `user.must_change_password === true`. Until the user picks a
 * new password the rest of the app is unreachable (gated by `RoleRoute`
 * in `App.js`). After success, the backend clears the flag and we refetch
 * the user so React-Router lets them through.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";

export default function ForceChangePassword() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const homeForRole = {
    admin:    "/admin",
    employee: "/funcionario",
    client:   "/cliente",
  };

  async function submit(e) {
    e?.preventDefault?.();
    if (pw1.length < 6) {
      toast.error("A nova password deve ter pelo menos 6 caracteres.");
      return;
    }
    if (pw1 !== pw2) {
      toast.error("As passwords não coincidem.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/me", { password: pw1 });
      toast.success("Password alterada com sucesso.");
      // Pull the fresh user (must_change_password cleared) so the route
      // guard lets us into the role's home page.
      await refreshMe?.();
      nav(homeForRole[user?.role] || "/", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#041f1b] grid place-items-center p-4"
         data-testid="force-change-password">
      <form onSubmit={submit}
            className="lux-card w-full max-w-md p-8 space-y-6">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-sm bg-[#d4af37]/15 border border-[#d4af37]/40 grid place-items-center text-[#d4af37] shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div>
            <div className="eyebrow">Segurança</div>
            <h1 className="font-display text-2xl text-[#fdfbf7] mt-1">
              Defina a sua password
            </h1>
            <p className="text-xs text-[#a0b2af] mt-2 leading-relaxed">
              A sua conta foi criada pelo administrador com uma password
              temporária. Antes de continuar, escolha uma password sua,
              fácil de lembrar e que só você saiba.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#a0b2af]">Nova password</span>
            <div className="relative mt-1">
              <input type={show ? "text" : "password"}
                     value={pw1}
                     onChange={e => setPw1(e.target.value)}
                     required minLength={6}
                     className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 pr-9 text-[#fdfbf7] rounded-sm focus:border-[#d4af37] outline-none"
                     placeholder="No mínimo 6 caracteres"
                     data-testid="fc-pw1" />
              <button type="button" onClick={() => setShow(s => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#a0b2af]"
                      tabIndex={-1}
                      aria-label={show ? "Esconder" : "Mostrar"}>
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#a0b2af]">Confirmar password</span>
            <input type={show ? "text" : "password"}
                   value={pw2}
                   onChange={e => setPw2(e.target.value)}
                   required minLength={6}
                   className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 mt-1 text-[#fdfbf7] rounded-sm focus:border-[#d4af37] outline-none"
                   placeholder="Repita a nova password"
                   data-testid="fc-pw2" />
          </label>
        </div>

        <button type="submit" disabled={saving}
                className="w-full btn-gold py-3 text-sm uppercase tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="fc-submit">
          <KeyRound size={14} />
          {saving ? "A guardar…" : "Definir password"}
        </button>
        <p className="text-[10px] text-[#a0b2af] text-center">
          {user?.email}
        </p>
      </form>
    </div>
  );
}
